import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import useApi from "@/hooks/useApi";
import LoadingSpinner from "@/components/LoadingSpinner";
import { PrimaryBlueButton, SecondaryBlueButton } from "@velaro/velaro-component-lib";
import StarterWorkflowWizard from "../StarterWorkflowWizard";

// ─── Types ──────────────────────────────────────────────────────────────────

interface GorgiasStatus {
  hasMigratedMacros: boolean;
  macroCount: number;
  importedAt: string | null;
  hasAI: boolean;
}

interface ImportResult {
  success: boolean;
  imported: number;
  skipped: number;
  message?: string;
  error?: string;
}

// ─── Step indicators ─────────────────────────────────────────────────────────

const STEPS = [
  { id: 1, label: "Welcome" },
  { id: 2, label: "Import macros" },
  { id: 3, label: "Connect Shopify" },
  { id: 4, label: "Launch bot" }
];

function StepIndicator({ current }: { current: number }) {
  return (
    <div className="flex items-center gap-2 mb-6">
      {STEPS.map((step, i) => (
        <React.Fragment key={step.id}>
          <div className={`flex items-center gap-1.5`}>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0
              ${current === step.id ? "bg-blue-600 text-white" :
                current > step.id ? "bg-green-500 text-white" :
                "bg-slate-200 text-slate-500"}`}>
              {current > step.id ? "✓" : step.id}
            </div>
            <span className={`text-xs whitespace-nowrap hidden sm:block ${
              current === step.id ? "text-slate-800 font-semibold" :
              current > step.id ? "text-green-600" :
              "text-slate-400"}`}>
              {step.label}
            </span>
          </div>
          {i < STEPS.length - 1 && (
            <div className={`h-px flex-1 ${current > step.id ? "bg-green-300" : "bg-slate-200"}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ─── Step 1: Welcome / comparison table ──────────────────────────────────────

function Step1Welcome({ onNext }: { onNext(): void }) {
  return (
    <div>
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-slate-800 mb-2">
          Switch from Gorgias to Velaro in 15 minutes
        </h2>
        <p className="text-slate-500 text-sm max-w-lg mx-auto">
          Import your canned responses, connect your Shopify store, and launch an AI bot that handles
          the questions your team was answering manually — automatically.
        </p>
      </div>

      {/* Comparison table */}
      <div className="rounded-xl border border-slate-200 overflow-hidden mb-6">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="px-4 py-3 text-left text-slate-600 font-semibold w-1/2">Feature</th>
              <th className="px-4 py-3 text-center text-slate-500 font-medium w-1/4">Gorgias</th>
              <th className="px-4 py-3 text-center text-blue-700 font-semibold w-1/4">Velaro</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {[
              { feature: "AI product comparison", gorgias: false, velaro: true },
              { feature: "Quote builder (checkout link)", gorgias: false, velaro: true },
              { feature: "Reads store automatically", gorgias: false, velaro: true },
              { feature: "Abandoned cart recovery", gorgias: false, velaro: true },
              { feature: "Live agent chat", gorgias: true, velaro: true },
              { feature: "Email/ticket support", gorgias: true, velaro: "soon" },
              { feature: "Price", gorgias: "$300+/mo", velaro: "From $129/mo" },
            ].map((row, i) => (
              <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50/50"}>
                <td className="px-4 py-3 text-slate-700">{row.feature}</td>
                <td className="px-4 py-3 text-center">
                  {typeof row.gorgias === "boolean" ? (
                    row.gorgias
                      ? <span className="text-green-600 font-bold">✓</span>
                      : <span className="text-slate-300">✗</span>
                  ) : (
                    <span className="text-slate-600 text-xs">{row.gorgias}</span>
                  )}
                </td>
                <td className="px-4 py-3 text-center">
                  {typeof row.velaro === "boolean" ? (
                    row.velaro
                      ? <span className="text-green-600 font-bold">✓</span>
                      : <span className="text-slate-300">✗</span>
                  ) : row.velaro === "soon" ? (
                    <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium">Coming soon</span>
                  ) : (
                    <span className="text-blue-700 font-semibold text-xs">{row.velaro}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end">
        <PrimaryBlueButton label="Start migration →" onClick={onNext} />
      </div>
    </div>
  );
}

// ─── Step 2: Import Gorgias macros ───────────────────────────────────────────

function Step2ImportMacros({ onNext, onSkip }: { onNext(): void; onSkip(): void }) {
  const api = useApi();
  const [accountName, setAccountName] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [showApiKeyHelp, setShowApiKeyHelp] = useState(false);

  async function handleImport() {
    if (!accountName.trim() || !apiKey.trim()) return;

    setLoading(true);
    setResult(null);
    try {
      const resp = await api.admin.post("Gorgias/ImportMacros", {
        gorgiasAccountName: accountName.trim(),
        apiKey: apiKey.trim()
      });
      const data = await resp.json();
      setResult(data);
      if (data?.success && (data.imported ?? 0) > 0) {
        // Auto-advance after a short delay so they can see the success
        setTimeout(() => onNext(), 2000);
      }
    } catch {
      setResult({ success: false, imported: 0, skipped: 0, error: "Request failed. Please try again." });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <h3 className="text-xl font-bold text-slate-800 mb-1">Import your canned responses (macros)</h3>
      <p className="text-slate-500 text-sm mb-4">
        Velaro will convert your Gorgias macros into bot Q&A pairs. Your bot can answer these questions
        automatically — no human needed.
      </p>

      <div className="space-y-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Gorgias account name
          </label>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={accountName}
              onChange={(e) => setAccountName(e.target.value)}
              placeholder="yourstore"
              className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <span className="text-slate-400 text-sm whitespace-nowrap">.gorgias.com</span>
          </div>
          <p className="text-xs text-slate-400 mt-1">
            This is the part before <code className="bg-slate-100 px-1 rounded">.gorgias.com</code> in your Gorgias URL.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            API key
          </label>
          <input
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder="••••••••••••••••••••"
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />

          {/* How to get API key accordion */}
          <div className="mt-2 rounded-lg border border-slate-200 overflow-hidden">
            <button
              type="button"
              onClick={() => setShowApiKeyHelp(!showApiKeyHelp)}
              className="w-full flex items-center justify-between px-3 py-2 text-left bg-slate-50 hover:bg-slate-100 text-xs text-slate-600"
            >
              <span>How to get your Gorgias API key</span>
              <span>{showApiKeyHelp ? "▲" : "▼"}</span>
            </button>
            {showApiKeyHelp && (
              <div className="px-3 py-3 text-xs text-slate-600 bg-white border-t border-slate-100 space-y-1.5">
                <p>1. Log in to your Gorgias account</p>
                <p>2. Go to <strong>Settings</strong> → <strong>REST API</strong></p>
                <p>3. Click <strong>Generate API key</strong></p>
                <p>4. Copy the key and paste it here</p>
                <p className="text-slate-400 mt-1">
                  Required scope: Read access to Macros is sufficient.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {result && (
        <div className={`rounded-lg p-3 mb-4 text-sm ${
          result.success ? "bg-green-50 border border-green-200 text-green-800" : "bg-red-50 border border-red-200 text-red-800"
        }`}>
          {result.success
            ? result.imported === 0
              ? (result.message ?? "No importable macros found.")
              : `Imported ${result.imported} macro${result.imported === 1 ? "" : "s"} as bot Q&A${result.skipped > 0 ? ` (${result.skipped} skipped — no message action)` : ""}. Continuing...`
            : (result.error ?? "Import failed. Please try again.")}
        </div>
      )}

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={onSkip}
          className="text-sm text-slate-400 hover:text-slate-600 underline"
        >
          Skip this step
        </button>
        <div className="flex gap-2">
          <PrimaryBlueButton
            label={loading ? "Importing..." : "Import macros"}
            onClick={handleImport}
            disabled={loading || !accountName.trim() || !apiKey.trim()}
          />
        </div>
      </div>
    </div>
  );
}

// ─── Step 3: Connect Shopify ──────────────────────────────────────────────────

function Step3ConnectShopify({ onNext }: { onNext(): void }) {
  const navigate = useNavigate();
  const api = useApi();
  const [shopifyConnected, setShopifyConnected] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);

  const checkShopify = useCallback(async () => {
    try {
      const resp = await api.admin.get("Shopify/Status");
      const data = await resp.json();
      const connected = data?.connected === true || data?.hasToken === true;
      setShopifyConnected(connected);
      if (connected) {
        // Auto-advance after a moment
        setTimeout(() => onNext(), 1500);
      }
    } catch {
      setShopifyConnected(false);
    } finally {
      setLoading(false);
    }
  }, [api.admin, onNext]);

  useEffect(() => { checkShopify(); }, [checkShopify]);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div>
      <h3 className="text-xl font-bold text-slate-800 mb-1">Connect your Shopify store</h3>
      <p className="text-slate-500 text-sm mb-6">
        After connecting Shopify, the bot will be able to look up orders, track shipments, and handle
        returns — everything Gorgias required a human agent for.
      </p>

      {shopifyConnected ? (
        <div className="rounded-xl bg-green-50 border border-green-200 p-6 text-center mb-6">
          <div className="text-4xl mb-2">✓</div>
          <div className="text-lg font-semibold text-green-800 mb-1">Shopify connected</div>
          <div className="text-sm text-green-600">Moving to next step...</div>
        </div>
      ) : (
        <>
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 mb-6">
            <div className="font-semibold text-amber-800 mb-1">Shopify not connected yet</div>
            <p className="text-sm text-amber-700">
              Connect your Shopify store first, then come back to continue the migration wizard.
            </p>
          </div>

          <div className="rounded-lg bg-blue-50 border border-blue-200 p-4 mb-6 text-sm text-blue-800">
            <strong>What you'll unlock:</strong>
            <ul className="list-disc list-inside mt-1.5 space-y-0.5 text-xs">
              <li>Order lookup — visitor asks "where is my order?" → bot answers live</li>
              <li>Product search — bot searches your catalog in real time</li>
              <li>Cart builder — bot creates checkout links with selected products</li>
              <li>Abandoned cart recovery — bot re-engages visitors who left mid-checkout</li>
              <li>Return handling — bot reads your policy and initiates return flow</li>
            </ul>
          </div>

          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={onNext}
              className="text-sm text-slate-400 hover:text-slate-600 underline"
            >
              Skip — I'll connect later
            </button>
            <div className="flex gap-2">
              <SecondaryBlueButton label="Refresh" onClick={() => { setLoading(true); checkShopify(); }} />
              <PrimaryBlueButton
                label="Connect Shopify →"
                onClick={() => navigate("/integrations/Shopify")}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Step 4: Launch the bot ───────────────────────────────────────────────────

function Step4Launch({ onOpenWizard }: { onOpenWizard(): void }) {
  const navigate = useNavigate();

  return (
    <div>
      <div className="text-center mb-6">
        <div className="text-5xl mb-3">🚀</div>
        <h3 className="text-xl font-bold text-slate-800 mb-1">You're ready to go live</h3>
        <p className="text-slate-500 text-sm">
          Three actions to launch your Velaro bot and start handling conversations automatically.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {/* Card 1: Scan store */}
        <button
          type="button"
          onClick={() => navigate("/integrations/Shopify")}
          className="text-left rounded-xl border border-slate-200 p-4 hover:border-blue-300 hover:bg-blue-50 transition-colors group"
        >
          <div className="text-2xl mb-2">🔍</div>
          <div className="font-semibold text-slate-800 text-sm mb-1 group-hover:text-blue-700">
            Scan your store
          </div>
          <p className="text-xs text-slate-500">
            Let the bot read your products, collections, and policies automatically.
          </p>
        </button>

        {/* Card 2: Review Q&A */}
        <button
          type="button"
          onClick={() => navigate("/integrations/Shopify")}
          className="text-left rounded-xl border border-slate-200 p-4 hover:border-blue-300 hover:bg-blue-50 transition-colors group"
        >
          <div className="text-2xl mb-2">💬</div>
          <div className="font-semibold text-slate-800 text-sm mb-1 group-hover:text-blue-700">
            Review imported Q&A
          </div>
          <p className="text-xs text-slate-500">
            View and edit the Gorgias macros that were converted to bot Q&A pairs.
          </p>
        </button>

        {/* Card 3: Launch wizard */}
        <button
          type="button"
          onClick={onOpenWizard}
          className="text-left rounded-xl border border-blue-200 bg-blue-50 p-4 hover:border-blue-400 hover:bg-blue-100 transition-colors group"
        >
          <div className="text-2xl mb-2">⚡</div>
          <div className="font-semibold text-blue-700 text-sm mb-1">
            Launch Starter Wizard
          </div>
          <p className="text-xs text-blue-600">
            Create a ready-to-use Shopify support workflow with 3 clicks.
          </p>
        </button>
      </div>

      <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 text-xs text-slate-500">
        <strong className="text-slate-600">Cancelling Gorgias:</strong> Your Gorgias subscription can be cancelled
        after you confirm the bot handles your volume. Most merchants see full coverage within 2 weeks —
        start by running both in parallel, then cancel Gorgias once you're confident.
      </div>
    </div>
  );
}

// ─── Main Wizard Component ────────────────────────────────────────────────────

export default function GorgiasWizard() {
  const [step, setStep] = useState(1);
  const [showWorkflowWizard, setShowWorkflowWizard] = useState(false);

  return (
    <div className="max-w-2xl mx-auto">
      {showWorkflowWizard && (
        <StarterWorkflowWizard
          integration="shopify"
          onClose={() => setShowWorkflowWizard(false)}
        />
      )}

      <div className="bg-white rounded-2xl shadow-md p-6 mb-4">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center flex-shrink-0">
            <span className="text-white text-lg font-bold">G</span>
          </div>
          <div>
            <div className="font-bold text-slate-800 text-lg leading-tight">
              Gorgias → Velaro Migration
            </div>
            <div className="text-xs text-slate-400">Import macros, connect Shopify, launch bot</div>
          </div>
        </div>

        <StepIndicator current={step} />

        {step === 1 && <Step1Welcome onNext={() => setStep(2)} />}
        {step === 2 && (
          <Step2ImportMacros
            onNext={() => setStep(3)}
            onSkip={() => setStep(3)}
          />
        )}
        {step === 3 && <Step3ConnectShopify onNext={() => setStep(4)} />}
        {step === 4 && <Step4Launch onOpenWizard={() => setShowWorkflowWizard(true)} />}
      </div>

      {/* Navigation footer (back button when past step 1) */}
      {step > 1 && step < 4 && (
        <div className="flex justify-start">
          <button
            type="button"
            onClick={() => setStep((s) => s - 1)}
            className="text-sm text-slate-400 hover:text-slate-600"
          >
            ← Back
          </button>
        </div>
      )}
    </div>
  );
}

export function BackToIntegrationLandingGorgias() {
  const navigate = useNavigate();
  return (
    <div
      className="flex cursor-pointer items-center gap-2 text-slate-600"
      onClick={() => navigate("/integrations")}
    >
      ← Back to Integrations
    </div>
  );
}
